import { forwardRef, useImperativeHandle, useRef } from "react";
import { createPortal } from "react-dom";
import { C_ON_SURFACE, C_OUTLINE } from "~/common/components/icon/Colors";
import { GlobalStyles } from "~/common/mui";

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "zoovu-zoe": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        region?: string;
        "api-key"?: string;
        locale?: string;
        sku?: string;
        mode?: string;
        display?: string;
        feedback?: string;
      };
    }
  }
}

interface ZoovuZoeProps {
  region?: string;
  locale?: string;
  sku?: string;
  display?: "on-page" | "popup";
}

export interface ZoovuZoeRef {
  open: () => void;
}

export const ZoovuZoe = forwardRef<ZoovuZoeRef, ZoovuZoeProps>(({ display = "on-page", ...props }, ref) => {
  const zoeRef = useRef<HTMLElement>(null);
  const { sku } = props;

  useImperativeHandle(
    ref,
    () => ({
      open() {
        zoeRef.current?.dispatchEvent(new Event("show-zoovu-zoe"));
      },
    }),
    [],
  );

  const zoovuZoe = (
    <>
      <GlobalStyles
        styles={(theme) => ({
          "zoovu-zoe::part(conversation)": {
            "--base-color-inverted": theme.palette.text.primary,
          },
          "zoovu-zoe": {
            "--primary-color": C_OUTLINE,
            width: display == "popup" ? 0 : "auto",
          },
          "zoovu-zoe::part(popup)": {
            position: "fixed",
            margin: "auto",
            "transform-origin": "0 100%",
            "max-width": display == "popup" ? "780px" : "auto",
            "min-height": display == "popup" ? "calc(100% - 52px)" : "auto",
            border: `1px solid ${theme.palette.divider}`,
            "background-color": "white",
            "border-radius": "28px",
            color: theme.palette.textSecondary,
            padding: 0,

            [theme.breakpoints.down("tablet")]: {
              border: `none !important`,
              height: "80vh !important",
              "min-height": "80vh !important",
              "max-height": "80vh !important",
              bottom: "0 !important",
              top: "auto !important",
              left: "0 !important",
              right: "0 !important",
              margin: "0 !important",
              "transform-origin": "center bottom !important",
              "border-radius": "28px 28px 0 0 !important",
            },
          },
          "zoovu-zoe::part(title)": {
            color: theme.palette.text.secondary,
          },
          "zoovu-zoe::part(input)": {
            color: theme.palette.textSecondary,
            borderRadius: "4px",
            border: `1px solid ${theme.palette.divider}`,
            height: "56px",
          },
          "zoovu-zoe::part(input)::placeholder": {
            color: theme.palette.textLabel.main,
          },
          "zoovu-zoe::part(popup-button)": {
            display: "none",
          },
          "zoovu-zoe::part(popup-backdrop)": {
            opacity: 0.6,
            "background-color": C_ON_SURFACE,
          },
          "zoovu-zoe::part(chat)": {
            gap: "0px",
          },
          "zoovu-zoe::part(chat-message)": {
            "border-radius": "8px",
            color: theme.palette.text.secondary,
          },
          "zoovu-zoe::part(user-message)": {
            backgroundColor: theme.palette.primary.container,
          },
          "zoovu-zoe::part(zoe-message)": {
            backgroundColor: "white",
            "padding-left": 0,
          },
          "zoovu-zoe::part(feedback)": {
            "padding-left": 0,
          },
          "zoovu-zoe::part(actions-option)": {
            "border-radius": "8px",
            color: theme.palette.text.secondary,
            "background-color": theme.palette.primary.container,
            "border-color": theme.palette.divider,
          },
          "zoovu-zoe::part(suggested-questions)": {
            "margin-right": "calc(var(--conversation-indent) * -1)", //parent of this class does not have "part"
            "flex-direction": "column",
            "align-items": "flex-end",
          },
          "zoovu-zoe::part(product)": {
            "background-color": "white",
          },
        })}
      />

      <zoovu-zoe
        key={sku}
        ref={zoeRef}
        api-key="BMOJR5kxQH7Hp3rA54cmloNSvU0"
        mode="full"
        display={display}
        feedback="true"
        sku={sku}
        {...props}
      />
    </>
  );

  if (display == "on-page") {
    return zoovuZoe;
  }

  return createPortal(zoovuZoe, document.body);
});
